import { useNavigate } from "@tanstack/react-router"

import type { EmailContact } from "@productlane/api"

import type { ReplyType, TabType } from "./types"
import type { Editor } from "@tiptap/react"
import { useShortcut } from "@/lib"
import { useBackNavigation } from "@/lib/back-navigation"
import { useRole } from "@/lib/useRole"
import { useThreadContext } from "../../../thread-context"

interface UseBottomBarShortcutsParams {
	composeEditor: Editor | null
	commentEditor: Editor | null
	activeTab: TabType
	replyType: ReplyType
	hasClickedReply: boolean
	send: () => Promise<void>
	switchTab: (tab: TabType) => void
	setIsForwardMode: (v: boolean) => void
	setForwardRecipients: (v: EmailContact[]) => void
	setHasClickedReply: (v: boolean) => void
	handleEmailContentPopulation: () => void
	onReplyAll: () => void
	onSelectPlainReply: () => void
	pendingMessages: string[]
	getMessageContent: (id: string) => string | undefined
	getUndoMessageFn: (id: string, content?: string) => () => Promise<void>
}

export function useBottomBarShortcuts({
	composeEditor,
	commentEditor,
	activeTab,
	replyType,
	hasClickedReply,
	send,
	switchTab,
	setIsForwardMode,
	setForwardRecipients,
	setHasClickedReply,
	handleEmailContentPopulation,
	onReplyAll,
	onSelectPlainReply,
	pendingMessages,
	getMessageContent,
	getUndoMessageFn,
}: UseBottomBarShortcutsParams) {
	const { role } = useRole()
	const navigate = useNavigate()
	const { goBack } = useBackNavigation()
	const { leaveOnEscape = true } = useThreadContext()

	useShortcut(
		"meta+enter, ctrl+enter",
		() => {
			if (composeEditor?.isFocused || commentEditor?.isFocused) {
				void send()
			}
		},
		{
			enableOnContentEditable: true,
		},
	)

	useShortcut(
		"g + r",
		() => {
			void navigate({ to: "/edit-requests" })
		},
		{ enabled: role !== "VIEWER" },
	)

	useShortcut(
		"r",
		() => {
			switchTab("reply")
			setIsForwardMode(false)
			onSelectPlainReply()
			if (composeEditor) {
				if (replyType === "email" && !hasClickedReply) {
					handleEmailContentPopulation()
					setHasClickedReply(true)
				}
				requestAnimationFrame(() => {
					composeEditor.commands.focus("end", { scrollIntoView: false })
				})
			}
		},
		{ enabled: role !== "VIEWER" },
	)

	useShortcut(
		"shift+r",
		() => {
			onReplyAll()
		},
		{ enabled: role !== "VIEWER" && replyType === "email" },
	)

	useShortcut("n", () => {
		switchTab("comment")
	})

	useShortcut(
		"w",
		() => {
			switchTab("reply")
			setIsForwardMode(true)
			setForwardRecipients([])
			setHasClickedReply(false)
		},
		{ enabled: role !== "VIEWER" && replyType === "email" },
	)

	useShortcut(
		"esc",
		() => {
			if (composeEditor?.isFocused) {
				composeEditor.commands.blur()
				return
			}
			if (commentEditor?.isFocused) {
				commentEditor.commands.blur()
				return
			}

			if (leaveOnEscape) goBack("/threads")
		},
		{ enableOnContentEditable: true },
	)

	useShortcut("enter", () => {
		if (activeTab === "reply" && composeEditor) {
			if (replyType === "email" && !hasClickedReply) {
				handleEmailContentPopulation()
				setHasClickedReply(true)
			}
			requestAnimationFrame(() => {
				composeEditor.commands.focus("end", { scrollIntoView: false })
			})
		} else if (activeTab === "comment" && commentEditor) {
			if (!commentEditor.isFocused) {
				commentEditor.commands.focus("end", { scrollIntoView: false })
			}
		}
	})

	useShortcut("z", () => {
		if (pendingMessages.length > 0) {
			const mostRecentMessageId = pendingMessages[pendingMessages.length - 1]
			if (mostRecentMessageId) {
				const content = getMessageContent(mostRecentMessageId)
				void getUndoMessageFn(mostRecentMessageId, content)()
			}
		}
	})
}
